import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Avatar,
    Divider,
    Link,
    Tooltip,
    CircularProgress,
    Alert,
    Stack,
    Skeleton,
    Paper,
    useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { formatTokenAmount, transformToBTC } from '../../utils/helpers';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';
import { useUserBalances } from '../../hooks';
import moment from 'moment';
import 'moment/locale/zh-cn';

// --- HELPER FUNCTIONS (for consistency with the explorer) ---
// const transformToBTC = (sats) => {
//     if (sats === null || sats === undefined || isNaN(sats)) return 'N/A';
//     const btc = sats / 100_000_000;
//     if (btc === 0) return '0.00';
//     if (btc < 0.01) return btc.toFixed(6);
//     return btc.toLocaleString(undefined, {
//         minimumFractionDigits: 2,
//         maximumFractionDigits: 4,
//     });
// };

const formatLargeNumber = (num) => (typeof num === 'number' ? num.toLocaleString('en-US') : 'N/A');

const formatDate = (dateString) =>
    dateString
        ? moment(dateString).format('MMM D, YYYY')
        : 'N/A';

const abbreviateString = (str, startChars = 8, endChars = 6) => {
    if (!str || str.length <= startChars + endChars) return str;
    return `${ str.substring(0, startChars) }...${ str.substring(str.length - endChars) }`;
};

// --- STYLED SUB-COMPONENTS ---

const PriceChange = ({ current, previous, label }) => {
    if (typeof current !== 'number' || typeof previous !== 'number' || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    const isUp = change >= 0;
    const color = isUp ? 'success.main' : 'error.main';
    const Icon = isUp ? ArrowUpward : ArrowDownward;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" component="span" sx={{ color, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <Icon sx={{ fontSize: '1rem', mr: 0.5 }} />
                {Math.abs(change).toFixed(1)}%
            </Typography>
        </Box>
    );
};

const DetailItem = ({ label, value, tooltip, valueIsMono = true }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
            {label}
            {tooltip && (
                <Tooltip title={tooltip} placement="top">
                    <InfoIcon sx={{ fontSize: 14, ml: 0.5 }} />
                </Tooltip>
            )}
        </Typography>
        <Typography sx={{ fontWeight: 'medium', fontFamily: valueIsMono ? 'monospace' : 'inherit' }}>{value}</Typography>
    </Box>
);

// --- MAIN DETAIL BAR COMPONENT ---

const Airdropped = ({ userId, tokenId }) => {
    const { t } = useTranslation();
    const { data, error, loading } = useUserBalances(userId, 1, encodeURIComponent(JSON.stringify([tokenId])));
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Skeleton variant="text" width={100} />
                <Skeleton variant="text" width={50} />
            </Box>
        );
    }
    if (error) {
        return (
            <Alert severity="error" sx={{ m: 0, borderRadius: 2 }}>
                {error}
            </Alert>
        );
    }
    if (!data || data.length === 0) return null;
    const balance = data.find(b => b.tokenid === tokenId);

    return <DetailItem label={t('token.airdropped')} value={balance && balance.airdroppedSent && balance.airdroppedSent > 0 ? formatTokenAmount(balance.airdroppedSent) : 0} tooltip={t('token.airdropTooltip')} />;
};

const TokenDetailBar = ({ tokenId }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { price: btcPrice } = useBitcoinPrice();

    // Set moment locale based on current language
    useEffect(() => {
        const locale = i18n.language === 'zh' ? 'zh-cn' : 'en';
        moment.locale(locale);
    }, [i18n.language]);

    useEffect(() => {
        if (!tokenId) {
            setLoading(false);
            setError('No Token ID provided.');
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            setData(null);
            try {
                const response = await fetch(`https://api.odin.fun/v1/token/${ tokenId }`);
                if (!response.ok) throw new Error(`Fetch failed: ${ response.status }`);
                const result = await response.json();
                setData(result);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [tokenId]);

    if (loading) {
        return (
            <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 2, border: `1px solid ${ theme.palette.divider }` }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={100} /></Grid>
                    <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={100} /></Grid>
                    <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={100} /></Grid>
                </Grid>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 0, borderRadius: 2 }}>
                {error}
            </Alert>
        );
    }
    if (!data) return null;

    const socialLink = (url, children) => (
        <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
            {children}
        </Link>
    );

    return (
        <Box sx={{ width: '100%', top: '16px', zIndex: 1100 }}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Grid container spacing={{ xs: 2, md: 4 }}>
                        {/* Column 1: Identity & Price */}
                        <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar src={`https://images.odin.fun/token/${ tokenId }`} sx={{ width: 64, height: 64 }} />
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                        {data.name} <span style={{ color: theme.palette.text.secondary }}>({data.ticker})</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                                        {data.description}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',flexDirection:"column" }}>
                                <Box sx={{ display: 'flex', justifyContent:"center",alignItems: 'center', width: '100%' }}>
                                    <Typography
                                        variant="h6"
                                        color="text.secondary"
                                        sx={{
                                            flexShrink: 0,
                                            minWidth: 60,
                                            textAlign: 'left',
                                            mr: 2,
                                        }}
                                    >
                                        {t('token.price')}
                                    </Typography>
                                   <Box>
                                     <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'primary.main',
                                            flexGrow: 1,
                                            textAlign: 'left',
                                        }}
                                    >
                                        {transformToBTC(data.price)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            ml: 2,
                                            flexShrink: 0,
                                            minWidth: 90,
                                            textAlign: 'right',
                                        }}
                                    >
                                        ${((data.price / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                                    </Typography>
                                   </Box>
                                </Box>
                                <Divider sx={{ my: 1, width: '100%' }} />
                                <Stack direction="row" spacing={3} alignItems="center">
                                    <PriceChange current={data.price} previous={data.price_1h} label="1h" />
                                    <PriceChange current={data.price} previous={data.price_6h} label="6h" />
                                    <PriceChange current={data.price} previous={data.price_1d} label="1d" />
                                </Stack>
                            </Box>
                        </Grid>

                        {/* Column 2: Market & Supply */}
                        <Grid item xs={12} md={4} sx={{
                            borderLeft: { md: `1px solid ${ theme.palette.divider }` },
                            borderRight: { md: `1px solid ${ theme.palette.divider }` },
                            px: { md: 4 }
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('token.marketCap')}
                                </Typography>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>{transformToBTC(data.marketcap)}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ${((data.marketcap / 1e8) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </Typography>
                                </Box>
                            </Box>
                            <DetailItem label={t('token.totalSupply')} value={formatLargeNumber(data.total_supply / 1e11)} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('token.liquidity')}
                                </Typography>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>{transformToBTC(data.btc_liquidity)}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ${((data.btc_liquidity / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </Typography>
                                </Box>
                            </Box>
                            <DetailItem label={t('token.holders')} value={formatLargeNumber(data.holder_count)} />
                            <DetailItem label={t('token.totalTxs')} value={formatLargeNumber(data.txn_count)} />
                        </Grid>

                        {/* Column 3: Details & Links */}
                        <Grid item xs={12} md={4}>
                            <Airdropped userId={data.creator} tokenId={tokenId} />
                            <DetailItem label={t('token.rune')} value={data.rune} valueIsMono={false} />
                            <DetailItem label={t('token.creator')} value={abbreviateString(data.creator)} />
                            <DetailItem label={t('token.created')} value={formatDate(data.created_time)} valueIsMono={false} />
                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                                {data.verified && <Chip label={t('token.verified')} size="small" variant="outlined" color="success" />}
                                {data.bonded && <Chip label={t('token.bonded')} size="small" variant="outlined" color="info" />}
                                {data.trading && <Chip label={t('token.trading')} size="small" variant="outlined" color="warning" />}
                            </Stack>
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                {data.twitter && socialLink(data.twitter, 'Twitter')}
                                {data.website && socialLink(data.website, t('token.website'))}
                                {data.telegram && socialLink(data.telegram, 'Telegram')}
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </Paper>
        </Box>
    );
};

export default TokenDetailBar;